/**
 * Field Mappings Configuration
 *
 * Defines preset mappings for data-bound template fields.
 * Each field has a fallback chain that tries sources in order until one returns a value.
 *
 * These mappings enable the API to resolve field values at save time without
 * requiring users to manually configure data sources.
 */

// ============================================================================
// TYPES
// ============================================================================

export type FieldSource = 'quattr' | 'tag' | 'customer' | 'static';

export interface FieldMappingSource {
  source: FieldSource;
  field: string; // Field name in that source (e.g., 'og:image', 'schema:author')
}

export interface FieldTransform {
  type: 'date' | 'truncate' | 'currency' | 'number' | 'uppercase' | 'lowercase';
  options?: Record<string, unknown>;
}

export interface PresetFieldMapping {
  /** Unique identifier for this field */
  id: string;

  /** Display name shown in UI */
  displayName: string;

  /** Variable name used in HTML templates (e.g., {{image}}) */
  templateVariable: string;

  /** Ordered list of sources to try (first match wins) */
  chain: FieldMappingSource[];

  /** Default value if all sources fail (null = hide the field) */
  fallbackValue: string | null;

  /** Optional transformation to apply to the resolved value */
  transform?: FieldTransform;

  /** Description for advanced users */
  description?: string;
}

// ============================================================================
// PRESET FIELD MAPPINGS
// ============================================================================

/**
 * Standard field mappings with smart defaults.
 * These cover the most common data fields used in rich templates.
 */
export const PRESET_FIELD_MAPPINGS: Record<string, PresetFieldMapping> = {
  // -------------------------------------------------------------------------
  // IMAGE FIELDS
  // -------------------------------------------------------------------------
  image: {
    id: 'image',
    displayName: 'Featured Image',
    templateVariable: 'image',
    chain: [
      { source: 'quattr', field: 'og:image' },
      { source: 'quattr', field: 'schema:image' },
      { source: 'quattr', field: 'twitter:image' },
      { source: 'quattr', field: 'computed:primaryImage' },
    ],
    fallbackValue: null, // Hide image if not found
    description: 'Main image for the page, typically from Open Graph or Schema.org',
  },

  thumbnail: {
    id: 'thumbnail',
    displayName: 'Thumbnail Image',
    templateVariable: 'thumbnail',
    chain: [
      { source: 'quattr', field: 'og:image' },
      { source: 'quattr', field: 'schema:image' },
      { source: 'quattr', field: 'computed:primaryImage' },
    ],
    fallbackValue: null,
    description: 'Smaller image for card/list displays',
  },

  // -------------------------------------------------------------------------
  // TITLE FIELDS
  // -------------------------------------------------------------------------
  title: {
    id: 'title',
    displayName: 'Page Title',
    templateVariable: 'title',
    chain: [
      { source: 'quattr', field: 'og:title' },
      { source: 'quattr', field: 'schema:headline' },
      { source: 'quattr', field: 'meta:title' },
      { source: 'quattr', field: 'h1' },
    ],
    fallbackValue: '{{anchorText}}', // Use anchor text as ultimate fallback
    description: 'Page title, preferring Open Graph title',
  },

  // -------------------------------------------------------------------------
  // DESCRIPTION FIELDS
  // -------------------------------------------------------------------------
  description: {
    id: 'description',
    displayName: 'Description',
    templateVariable: 'description',
    chain: [
      { source: 'quattr', field: 'og:description' },
      { source: 'quattr', field: 'schema:description' },
      { source: 'quattr', field: 'meta:description' },
    ],
    fallbackValue: null,
    transform: { type: 'truncate', options: { length: 160 } },
    description: 'Page description/excerpt',
  },

  excerpt: {
    id: 'excerpt',
    displayName: 'Excerpt',
    templateVariable: 'excerpt',
    chain: [
      { source: 'quattr', field: 'schema:description' },
      { source: 'quattr', field: 'og:description' },
      { source: 'quattr', field: 'meta:description' },
    ],
    fallbackValue: null,
    transform: { type: 'truncate', options: { length: 120 } },
    description: 'Short excerpt for article cards',
  },

  // -------------------------------------------------------------------------
  // AUTHOR FIELDS
  // -------------------------------------------------------------------------
  author: {
    id: 'author',
    displayName: 'Author',
    templateVariable: 'author',
    chain: [
      { source: 'quattr', field: 'schema:author' },
      { source: 'quattr', field: 'og:author' },
      { source: 'quattr', field: 'meta:author' },
      { source: 'quattr', field: 'twitter:creator' },
    ],
    fallbackValue: null, // Hide author if not found
    description: 'Content author name',
  },

  // -------------------------------------------------------------------------
  // DATE FIELDS
  // -------------------------------------------------------------------------
  publishDate: {
    id: 'publishDate',
    displayName: 'Publish Date',
    templateVariable: 'publishDate',
    chain: [
      { source: 'quattr', field: 'schema:datePublished' },
      { source: 'quattr', field: 'og:publishedTime' },
      { source: 'quattr', field: 'meta:publishDate' },
    ],
    fallbackValue: null,
    transform: { type: 'date', options: { format: 'MMM D, YYYY' } },
    description: 'Original publication date',
  },

  modifiedDate: {
    id: 'modifiedDate',
    displayName: 'Modified Date',
    templateVariable: 'modifiedDate',
    chain: [
      { source: 'quattr', field: 'schema:dateModified' },
      { source: 'quattr', field: 'og:modifiedTime' },
    ],
    fallbackValue: null,
    transform: { type: 'date', options: { format: 'MMM D, YYYY' } },
    description: 'Last modification date',
  },

  // -------------------------------------------------------------------------
  // COMPUTED/DERIVED FIELDS
  // -------------------------------------------------------------------------
  readingTime: {
    id: 'readingTime',
    displayName: 'Reading Time',
    templateVariable: 'readingTime',
    chain: [{ source: 'quattr', field: 'computed:readingTime' }],
    fallbackValue: null,
    description: 'Estimated reading time (calculated from word count)',
  },

  wordCount: {
    id: 'wordCount',
    displayName: 'Word Count',
    templateVariable: 'wordCount',
    chain: [{ source: 'quattr', field: 'computed:wordCount' }],
    fallbackValue: null,
    description: 'Total word count of page content',
  },

  // -------------------------------------------------------------------------
  // CATEGORY/TAXONOMY FIELDS
  // -------------------------------------------------------------------------
  category: {
    id: 'category',
    displayName: 'Category',
    templateVariable: 'category',
    chain: [
      { source: 'quattr', field: 'schema:articleSection' },
      { source: 'quattr', field: 'og:section' },
      { source: 'tag', field: 'Category' }, // From Tag Groups
    ],
    fallbackValue: null,
    description: 'Content category or section',
  },

  tags: {
    id: 'tags',
    displayName: 'Tags',
    templateVariable: 'tags',
    chain: [
      { source: 'quattr', field: 'schema:keywords' },
      { source: 'quattr', field: 'meta:keywords' },
      { source: 'quattr', field: 'og:tags' },
    ],
    fallbackValue: null,
    description: 'Content tags/keywords',
  },

  // -------------------------------------------------------------------------
  // E-COMMERCE FIELDS
  // -------------------------------------------------------------------------
  price: {
    id: 'price',
    displayName: 'Price',
    templateVariable: 'price',
    chain: [
      { source: 'quattr', field: 'schema:price' },
      { source: 'quattr', field: 'schema:offers.price' },
    ],
    fallbackValue: null,
    transform: { type: 'currency', options: { currency: 'USD' } },
    description: 'Product price',
  },

  currency: {
    id: 'currency',
    displayName: 'Currency',
    templateVariable: 'currency',
    chain: [
      { source: 'quattr', field: 'schema:priceCurrency' },
      { source: 'quattr', field: 'schema:offers.priceCurrency' },
    ],
    fallbackValue: 'USD',
    description: 'Price currency code',
  },

  brand: {
    id: 'brand',
    displayName: 'Brand',
    templateVariable: 'brand',
    chain: [
      { source: 'quattr', field: 'schema:brand' },
      { source: 'quattr', field: 'schema:brand.name' },
    ],
    fallbackValue: null,
    description: 'Product brand name',
  },

  rating: {
    id: 'rating',
    displayName: 'Rating',
    templateVariable: 'rating',
    chain: [
      { source: 'quattr', field: 'schema:aggregateRating.ratingValue' },
      { source: 'quattr', field: 'schema:ratingValue' },
    ],
    fallbackValue: null,
    description: 'Average rating value',
  },

  reviewCount: {
    id: 'reviewCount',
    displayName: 'Review Count',
    templateVariable: 'reviewCount',
    chain: [
      { source: 'quattr', field: 'schema:aggregateRating.reviewCount' },
      { source: 'quattr', field: 'schema:reviewCount' },
    ],
    fallbackValue: null,
    description: 'Number of reviews',
  },

  availability: {
    id: 'availability',
    displayName: 'Availability',
    templateVariable: 'availability',
    chain: [
      { source: 'quattr', field: 'schema:availability' },
      { source: 'quattr', field: 'schema:offers.availability' },
    ],
    fallbackValue: null,
    description: 'Product availability status',
  },

  sku: {
    id: 'sku',
    displayName: 'SKU',
    templateVariable: 'sku',
    chain: [
      { source: 'quattr', field: 'schema:sku' },
      { source: 'quattr', field: 'schema:productID' },
    ],
    fallbackValue: null,
    description: 'Product SKU/identifier',
  },

  // -------------------------------------------------------------------------
  // VIDEO FIELDS
  // -------------------------------------------------------------------------
  videoDuration: {
    id: 'videoDuration',
    displayName: 'Video Duration',
    templateVariable: 'duration',
    chain: [
      { source: 'quattr', field: 'schema:duration' },
      { source: 'quattr', field: 'og:video:duration' },
    ],
    fallbackValue: null,
    description: 'Video duration',
  },

  videoThumbnail: {
    id: 'videoThumbnail',
    displayName: 'Video Thumbnail',
    templateVariable: 'videoThumbnail',
    chain: [
      { source: 'quattr', field: 'schema:thumbnailUrl' },
      { source: 'quattr', field: 'og:image' },
    ],
    fallbackValue: null,
    description: 'Video thumbnail image',
  },

  channelName: {
    id: 'channelName',
    displayName: 'Channel Name',
    templateVariable: 'channelName',
    chain: [
      { source: 'quattr', field: 'schema:author.name' },
      { source: 'quattr', field: 'og:site_name' },
    ],
    fallbackValue: null,
    description: 'Video channel/creator name',
  },

  viewCount: {
    id: 'viewCount',
    displayName: 'View Count',
    templateVariable: 'viewCount',
    chain: [{ source: 'quattr', field: 'schema:interactionCount' }],
    fallbackValue: null,
    transform: { type: 'number', options: { format: 'compact' } },
    description: 'Video view count',
  },

  // -------------------------------------------------------------------------
  // PUBLISHER/SITE FIELDS
  // -------------------------------------------------------------------------
  siteName: {
    id: 'siteName',
    displayName: 'Site Name',
    templateVariable: 'siteName',
    chain: [
      { source: 'quattr', field: 'og:site_name' },
      { source: 'quattr', field: 'schema:publisher.name' },
    ],
    fallbackValue: null,
    description: 'Publisher or site name',
  },

  favicon: {
    id: 'favicon',
    displayName: 'Favicon',
    templateVariable: 'favicon',
    chain: [{ source: 'quattr', field: 'computed:favicon' }],
    fallbackValue: null,
    description: 'Site favicon URL',
  },
};

// ============================================================================
// TEMPLATE FIELD REQUIREMENTS
// ============================================================================

/**
 * Maps template types to the data fields they require.
 * This determines which fields need to be resolved for each template.
 */
export const TEMPLATE_REQUIRED_FIELDS: Record<string, string[]> = {
  // Link list templates - minimal fields (just use anchorText + url)
  'vertical-link-list': [],
  'multi-column-links': [],
  'sidebar-link-box': [],
  'inline-reading-callout': [],
  'related-searches': [],

  // Card templates - need images, titles, descriptions
  'image-card-grid': ['image', 'title', 'description'],
  'rich-article-cards': ['image', 'title', 'description', 'category', 'publishDate', 'readingTime', 'author'],
  'sidebar-card-stack': ['thumbnail', 'title', 'category'],
  'contextual-link-box': ['image', 'title', 'description', 'h1', 'author', 'readingTime', 'publishDate'],

  // Carousel templates
  'image-carousel': ['image', 'title'],
  'related-videos-carousel': ['videoThumbnail', 'title', 'videoDuration', 'channelName', 'viewCount'],
};

/**
 * Maps template config fields (checkboxes) to data fields.
 * When a config option is enabled, the corresponding data field is required.
 */
export const CONFIG_TO_FIELD_MAPPING: Record<string, string> = {
  // Image visibility
  showImage: 'image',
  showThumbnail: 'thumbnail',

  // Content fields
  showTitle: 'title',
  showDescription: 'description',
  showExcerpt: 'excerpt',

  // Metadata
  showAuthor: 'author',
  showDate: 'publishDate',
  showReadingTime: 'readingTime',
  showCategory: 'category',

  // E-commerce
  showPrice: 'price',
  showRating: 'rating',
  showBrand: 'brand',

  // Video
  showDuration: 'videoDuration',
  showChannel: 'channelName',
  showViews: 'viewCount',
  showPlayButton: 'videoThumbnail',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get the preset mapping for a field ID.
 */
export function getFieldMapping(fieldId: string): PresetFieldMapping | undefined {
  return PRESET_FIELD_MAPPINGS[fieldId];
}

/**
 * Get all required field mappings for a template based on its config.
 */
export function getRequiredFieldsForTemplate(
  templateId: string,
  config: Record<string, unknown>
): PresetFieldMapping[] {
  const requiredFieldIds = new Set<string>();

  // Add base required fields for the template
  const baseFields = TEMPLATE_REQUIRED_FIELDS[templateId] || [];
  baseFields.forEach((f) => requiredFieldIds.add(f));

  // Add fields based on enabled config options
  for (const [configKey, fieldId] of Object.entries(CONFIG_TO_FIELD_MAPPING)) {
    if (config[configKey] === true) {
      requiredFieldIds.add(fieldId);
    }
  }

  // Convert to mapping objects
  return Array.from(requiredFieldIds)
    .map((id) => PRESET_FIELD_MAPPINGS[id])
    .filter((m): m is PresetFieldMapping => m !== undefined);
}

/**
 * Generate the field resolution specification for API requests.
 * This tells the backend which fields to resolve and how.
 */
export function generateFieldResolutionSpec(
  templateId: string,
  config: Record<string, unknown>
): {
  fields: Array<{
    templateVariable: string;
    chain: FieldMappingSource[];
    fallbackValue: string | null;
    transform?: FieldTransform;
  }>;
} {
  const requiredMappings = getRequiredFieldsForTemplate(templateId, config);

  return {
    fields: requiredMappings.map((mapping) => ({
      templateVariable: mapping.templateVariable,
      chain: mapping.chain,
      fallbackValue: mapping.fallbackValue,
      transform: mapping.transform,
    })),
  };
}

/**
 * Get a human-readable summary of field mappings for a template.
 * Useful for displaying "Using: og:image → schema:image" in UI.
 */
export function getFieldMappingSummary(fieldId: string): string {
  const mapping = PRESET_FIELD_MAPPINGS[fieldId];
  if (!mapping) return 'Unknown field';

  const sources = mapping.chain
    .slice(0, 2) // Show first 2 sources
    .map((s) => s.field)
    .join(' → ');

  const suffix = mapping.chain.length > 2 ? ' → ...' : '';
  return sources + suffix;
}

// ============================================================================
// QUATTR CRAWLER FIELD DEFINITIONS
// ============================================================================

/**
 * All fields that the Quattr crawler should extract.
 * This serves as the source of truth for crawler enhancements.
 */
export const QUATTR_CRAWLER_FIELDS = {
  // Open Graph
  'og:title': { type: 'string', source: 'meta', selector: 'meta[property="og:title"]' },
  'og:description': { type: 'string', source: 'meta', selector: 'meta[property="og:description"]' },
  'og:image': { type: 'url', source: 'meta', selector: 'meta[property="og:image"]' },
  'og:type': { type: 'string', source: 'meta', selector: 'meta[property="og:type"]' },
  'og:site_name': { type: 'string', source: 'meta', selector: 'meta[property="og:site_name"]' },
  'og:publishedTime': { type: 'date', source: 'meta', selector: 'meta[property="article:published_time"]' },
  'og:modifiedTime': { type: 'date', source: 'meta', selector: 'meta[property="article:modified_time"]' },
  'og:author': { type: 'string', source: 'meta', selector: 'meta[property="article:author"]' },
  'og:section': { type: 'string', source: 'meta', selector: 'meta[property="article:section"]' },
  'og:tags': { type: 'array', source: 'meta', selector: 'meta[property="article:tag"]' },
  'og:video:duration': { type: 'number', source: 'meta', selector: 'meta[property="video:duration"]' },

  // Twitter Cards
  'twitter:title': { type: 'string', source: 'meta', selector: 'meta[name="twitter:title"]' },
  'twitter:description': { type: 'string', source: 'meta', selector: 'meta[name="twitter:description"]' },
  'twitter:image': { type: 'url', source: 'meta', selector: 'meta[name="twitter:image"]' },
  'twitter:creator': { type: 'string', source: 'meta', selector: 'meta[name="twitter:creator"]' },

  // Standard Meta
  'meta:title': { type: 'string', source: 'html', selector: 'title' },
  'meta:description': { type: 'string', source: 'meta', selector: 'meta[name="description"]' },
  'meta:author': { type: 'string', source: 'meta', selector: 'meta[name="author"]' },
  'meta:keywords': { type: 'array', source: 'meta', selector: 'meta[name="keywords"]' },
  'meta:publishDate': { type: 'date', source: 'meta', selector: 'meta[name="publish-date"], meta[name="date"]' },
  h1: { type: 'string', source: 'html', selector: 'h1' },

  // Schema.org (JSON-LD)
  'schema:headline': { type: 'string', source: 'jsonld', path: 'headline' },
  'schema:description': { type: 'string', source: 'jsonld', path: 'description' },
  'schema:image': { type: 'url', source: 'jsonld', path: 'image' },
  'schema:author': { type: 'string', source: 'jsonld', path: 'author.name' },
  'schema:datePublished': { type: 'date', source: 'jsonld', path: 'datePublished' },
  'schema:dateModified': { type: 'date', source: 'jsonld', path: 'dateModified' },
  'schema:articleSection': { type: 'string', source: 'jsonld', path: 'articleSection' },
  'schema:keywords': { type: 'array', source: 'jsonld', path: 'keywords' },
  'schema:publisher.name': { type: 'string', source: 'jsonld', path: 'publisher.name' },

  // Schema.org - Product
  'schema:price': { type: 'number', source: 'jsonld', path: 'offers.price' },
  'schema:priceCurrency': { type: 'string', source: 'jsonld', path: 'offers.priceCurrency' },
  'schema:offers.price': { type: 'number', source: 'jsonld', path: 'offers.price' },
  'schema:offers.priceCurrency': { type: 'string', source: 'jsonld', path: 'offers.priceCurrency' },
  'schema:offers.availability': { type: 'string', source: 'jsonld', path: 'offers.availability' },
  'schema:brand': { type: 'string', source: 'jsonld', path: 'brand.name' },
  'schema:brand.name': { type: 'string', source: 'jsonld', path: 'brand.name' },
  'schema:sku': { type: 'string', source: 'jsonld', path: 'sku' },
  'schema:productID': { type: 'string', source: 'jsonld', path: 'productID' },
  'schema:aggregateRating.ratingValue': { type: 'number', source: 'jsonld', path: 'aggregateRating.ratingValue' },
  'schema:aggregateRating.reviewCount': { type: 'number', source: 'jsonld', path: 'aggregateRating.reviewCount' },
  'schema:ratingValue': { type: 'number', source: 'jsonld', path: 'aggregateRating.ratingValue' },
  'schema:reviewCount': { type: 'number', source: 'jsonld', path: 'aggregateRating.reviewCount' },
  'schema:availability': { type: 'string', source: 'jsonld', path: 'offers.availability' },

  // Schema.org - Video
  'schema:duration': { type: 'string', source: 'jsonld', path: 'duration' },
  'schema:thumbnailUrl': { type: 'url', source: 'jsonld', path: 'thumbnailUrl' },
  'schema:interactionCount': { type: 'number', source: 'jsonld', path: 'interactionStatistic.userInteractionCount' },

  // Computed fields (derived during crawl)
  'computed:wordCount': { type: 'number', source: 'computed', description: 'Word count of main body content' },
  'computed:readingTime': {
    type: 'string',
    source: 'computed',
    description: 'Estimated reading time (e.g., "5 min read")',
  },
  'computed:primaryImage': { type: 'url', source: 'computed', description: 'Best image candidate from page' },
  'computed:favicon': { type: 'url', source: 'computed', description: 'Site favicon URL' },
};

/**
 * Get list of all Quattr crawler fields that need to be extracted.
 * Used for crawler enhancement specifications.
 */
export function getAllCrawlerFields(): string[] {
  return Object.keys(QUATTR_CRAWLER_FIELDS);
}
